// src/features/sso-auth/store/authStore.ts
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { authService } from '@/services/authService';
import type { LocaleResult } from '@/services/authService';

interface User {
  name?: string;
  email?: string;
  sub?: string;
  oid?: string;
  country_code?: string;
  country_name?: string;
  default_taxonomy?: string;
}

interface DecodedToken {
  name?: string;
  email?: string;
  preferred_username?: string;
  sub?: string;
  oid?: string;
  exp?: number;
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  default_taxonomy: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  sessionChecked: boolean;

  locales: LocaleResult[];

  // Actions
  setTokens: (accessToken: string, refreshToken: string) => Promise<void>;
  setUser: (user: User) => void;
  logout: () => void;
  isTokenExpired: (token: string) => boolean;
  getUserFromToken: (token: string) => Promise<User | null>;
  checkSession: () => Promise<boolean>;
  getLocales: () => Promise<void>;
  changeLocale: (localeCode: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      user: null,
      default_taxonomy: null,
      accessToken: null,
      refreshToken: null,
      isLoading: false,
      sessionChecked: false,
      locales: [],

      setTokens: async (accessToken: string, refreshToken: string) => {
        const user = await get().getUserFromToken(accessToken);
        console.log('setTokens:', { accessToken, refreshToken, user });
        // Save to localStorage for consistency
        localStorage.setItem('access_token', accessToken);
        localStorage.setItem('refresh_token', refreshToken);
        set({
          accessToken,
          refreshToken,
          user,
          default_taxonomy: user?.default_taxonomy ?? null,
          isAuthenticated: true,
          isLoading: false,
          sessionChecked: true,
        });
      },

      setUser: (user: User) => {
        set({ user, default_taxonomy: user.default_taxonomy ?? null });
      },

      logout: () => {
        set({
          isAuthenticated: false,
          user: null,
          default_taxonomy: null,
          accessToken: null,
          refreshToken: null,
          isLoading: false,
          sessionChecked: true, // Session state is now determined (logged out)
        });
        // Clear localStorage
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        // Redirect to login
        window.location.href = '/login';
      },

      isTokenExpired: (token: string): boolean => {
        // Validate token before attempting to decode to avoid throwing errors
        const isLikelyJWT = (t: string) => /^[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+$/.test(t);
        if (!token || token === 'null' || token === 'undefined' || !isLikelyJWT(token)) {
          return true;
        }

        try {
          const decoded: DecodedToken = jwtDecode(token);
          if (typeof decoded.exp !== 'number') return true;

          const currentTime = Date.now() / 1000;
          return decoded.exp < currentTime;
        } catch (_error) {
          // Silently treat as expired if decoding fails
          return true;
        }
      },

      getUserFromToken: async (_token: string): Promise<User | null> => {
        try {
          const accessToken = localStorage.getItem('access_token');
          if (!accessToken) return null;
          const response = await axios.get(`${import.meta.env.VITE_BACKEND_URL}/api/v1/user`, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`,
            },
          });
          const data = response.data;
          return {
            ...data,
            name: `${data.first_name || ''} ${data.last_name || ''}`.trim() || data.username || 'Usuario',
            email: data.email || '',
            sub: data.id,
            oid: data.id,
          };
        } catch (error) {
          console.error('Error fetching user from API:', error);
          return null;
        }
      },

      checkSession: async (): Promise<boolean> => {
        set({ isLoading: true, sessionChecked: false });
        const { accessToken, refreshToken, isTokenExpired, logout, setTokens: localSetTokens } = get();
        if (!accessToken || !refreshToken) {
          set({ isAuthenticated: false, user: null, default_taxonomy: null, isLoading: false, sessionChecked: true });
          return false;
        }

        // Check if access token is still valid
        if (!isTokenExpired(accessToken)) {
          set({ isLoading: false, sessionChecked: true });
          return true;
        }

        // If access token is expired, try to refresh
        if (!isTokenExpired(refreshToken)) {
          try {
            // Usar axios en vez de fetch
            const axios = await import('axios');
            const response = await axios.default.post(`${import.meta.env.VITE_BACKEND_URL}/api/v1/user/login/refresh-token`, {
              refresh_token: refreshToken,
            }, {
              headers: {
                'Content-Type': 'application/json',
              },
            });
            if (response.data?.access_token && response.data.refresh_token) {
              void localSetTokens(response.data.access_token, response.data.refresh_token); // Actualiza tokens y usuario
              set({ isLoading: false, sessionChecked: true });
              return true;
            } else {
              logout(); // Esto ya setea sessionChecked: true
              return false;
            }
          } catch (error) {
            console.error('Error refreshing token:', error);
            logout(); // Esto ya setea sessionChecked: true
            return false;
          }
        } else {
          set({ isLoading: false, sessionChecked: true });
          return false;
        }
      },

      getLocales: async (): Promise<void> => {
        try {
          const locales = await authService.getLocale();
          set({ locales: locales.map(locale => ({ ...locale, code: locale.code.toLowerCase() })) });
        } catch (error) {
          console.error('Error getting locales:', error);
        }
      },

      changeLocale: async (localeCode: string): Promise<void> => {
        try {
          const { accessToken } = get();
          if (!accessToken) return;

          set({ isLoading: true });
          await authService.changeLocale(localeCode);
          const user = await get().getUserFromToken(accessToken);
          localStorage.setItem('user', JSON.stringify(user));
          window.location.reload();

        } catch (error) {
          console.error('Error changing locale:', error);
          set({ isLoading: false });
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        default_taxonomy: state.default_taxonomy,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        sessionChecked: state.sessionChecked, // Persist sessionChecked status
      }),
    }
  )
);
